import React, { useMemo } from 'react';
import type { ArtifactsPanelStyle } from '@api/nodeDefinitions';
import type { ProcessArtifactDto } from '@api/processes';
import { EmptyState, EMPTY_STATE_SIZE_XS } from '@aibuild/ui';
import ArtifactPreview from '../runStatus/ArtifactPreview';
import ArtifactDownloadList from './ArtifactDownloadList';
import DownloadAllArtifactsButton from './DownloadAllArtifactsButton';
import {
  ArtifactsHeader,
  ArtifactsStrip,
  FieldLabel,
  GeneratingOverlay,
  PreviewOverlayHost,
} from './nodeCommon.styled';
import { useEditorContext } from '../useEditorContext';
import { useNodeLiveArtifacts } from './useNodeLiveArtifacts';
import { useHeldNodeArtifacts } from './useHeldNodeArtifacts';

export interface ArtifactsPanelProps {
  readonly nodeId: string;
  readonly latestArtifacts: readonly ProcessArtifactDto[] | undefined;
  /**
   * Catalog-supplied panel variant. {@code PREVIEW} renders the in-flow
   * thumbnail strip (Task default); {@code LIST} renders the
   * downloadable rows (Output default); {@code NONE} suppresses the
   * panel entirely (decorative kinds).
   *
   * Named {@code variant} (not {@code style}) on purpose so the
   * editor's {@code no-restricted-syntax} ESLint rule — which forbids
   * inline {@code style={...}} attributes in favour of
   * styled-components — doesn't trip on the call site.
   */
  readonly variant: ArtifactsPanelStyle;
  /**
   * When provided, artifacts whose {@code portId} is in this set are
   * filtered out — they are being rendered alongside their matching
   * output declaration in {@link OutputRow} instead, so showing them
   * here would visually duplicate them. The remaining artifacts are
   * the "orphan" / legacy fallback set (no {@code portId}, or
   * referencing a port that no longer exists). When the filtered list
   * is empty the panel collapses entirely.
   */
  readonly portIdsToExclude?: ReadonlySet<string>;
  /**
   * Opt-in for generation nodes (image_gen / 3d_gen): hold the previously
   * shown preview through a regenerate and show a "generating" spinner
   * instead of flashing to a stale snapshot or blanking mid-run
   * (AIB-7181). Off for plain Task previews, which keep the live overlay.
   */
  readonly generationPreview?: boolean;
}

/**
 * Run-time artifacts panel rendered below the body form by
 * {@code ProcessNode}. Reads live artifacts off the editor context and
 * de-dupes them on top of the persisted snapshot, then dispatches to
 * the appropriate renderer based on the catalog-supplied
 * {@code artifactsPanel} style.
 *
 * <p>This consolidates what used to be two separate hand-coded blocks
 * (Task's preview strip + Output's download list). Future kinds can
 * pick either style by overriding {@code artifactsPanelStyle()} on the
 * backend definition — no FE component change.</p>
 */
const ArtifactsPanel: React.FC<ArtifactsPanelProps> = ({
  nodeId,
  latestArtifacts,
  variant,
  portIdsToExclude,
  generationPreview,
}) => {
  const { isNodeGenerating } = useEditorContext();
  // Both hooks always run (rules of hooks); we pick which list to show.
  // Generation nodes hold their last preview forward; everything else
  // keeps the existing live-overlay behaviour.
  const liveArtifacts = useNodeLiveArtifacts(nodeId, latestArtifacts);
  const heldArtifacts = useHeldNodeArtifacts(nodeId, latestArtifacts);
  const allArtifacts = generationPreview ? heldArtifacts : liveArtifacts;
  const generating = generationPreview === true && isNodeGenerating(nodeId);
  const artifacts = useMemo(() => {
    if (!portIdsToExclude || portIdsToExclude.size === 0) return allArtifacts;
    return allArtifacts.filter(
      (a) => !(a.portId && portIdsToExclude.has(a.portId)),
    );
  }, [allArtifacts, portIdsToExclude]);

  // When the panel is being used as the orphan fallback (caller passed
  // {@code portIdsToExclude}) and no orphans remain, collapse the
  // section entirely — including the "No artifacts yet" empty state —
  // because the per-output sections already make it clear nothing has
  // been produced.
  const isOrphanFallback = portIdsToExclude !== undefined;

  if (variant === 'NONE') return null;
  if (variant === 'PREVIEW') {
    if (artifacts.length === 0 && !generating) return null;
    return (
      <PreviewOverlayHost $reserve={artifacts.length === 0}>
        {artifacts.length > 0 && (
          <ArtifactsStrip data-testid={`process-node__artifacts-${nodeId}`}>
            {artifacts.map((a) => (
              <ArtifactPreview
                key={a.artifactId}
                artifact={a}
                dataTestId={`process-node__artifact-${nodeId}-${a.artifactId}`}
              />
            ))}
          </ArtifactsStrip>
        )}
        {generating && (
          <GeneratingOverlay
            aria-hidden="true"
            data-testid={`process-node__artifacts-generating-${nodeId}`}
          >
            <span className="material-symbols-rounded">progress_activity</span>
          </GeneratingOverlay>
        )}
      </PreviewOverlayHost>
    );
  }
  if (artifacts.length === 0 && isOrphanFallback) return null;
  return (
    <div>
      <ArtifactsHeader>
        <FieldLabel>Latest artifacts</FieldLabel>
        {!isOrphanFallback && (
          <DownloadAllArtifactsButton
            nodeId={nodeId}
            artifacts={artifacts}
            dataTestId={`process-node__artifacts-download-all-${nodeId}`}
          />
        )}
      </ArtifactsHeader>
      {artifacts.length === 0 ? (
        <EmptyState
          size={EMPTY_STATE_SIZE_XS}
          title="No artifacts yet — run this process to produce one."
          dataTestId={`process-node__artifacts-empty-${nodeId}`}
        />
      ) : (
        <ArtifactDownloadList
          artifacts={artifacts}
          dataTestId={`process-node__artifacts-${nodeId}`}
        />
      )}
    </div>
  );
};

export default ArtifactsPanel;
